import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { ArrowRight, Brain, ScanLine, Timer, Trophy, Radio, UtensilsCrossed, Globe2, MapPin, Sparkles, Tv, Wine, Loader2 } from "lucide-react";
import { LogoLockup, BrainTapLogo } from "@/components/Logo";
import { Watermark } from "@/components/Watermark";
import { API_URL } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/bf574bee-5af4-4eb0-9418-c6e676fd5edf/images/b5a07679afe1dc47e586900e40f4b381b2b3ca457584a870538b392f237befa6.jpeg";
const FOOD_IMG =
  "https://images.unsplash.com/photo-1619810816144-223f5b027aea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxwdWIlMjBmb29kJTIwZHJpbmslMjBzcGVjaWFsc3xlbnwwfHx8fDE3OTM1NzY3Mzl8MA&ixlib=rb-4.1.0&q=85";
const COUCH_IMG =
  "https://images.unsplash.com/photo-1758876200754-17a09a6c2728?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxwZW9wbGUlMjBwbGF5aW5nJTIwZ2FtZSUyMG9uJTIwcGhvbmUlMjBjb3VjaHxlbnwwfHx8fDE3OTM1NzY3Mzl8MA&ixlib=rb-4.1.0&q=85";

const MARQUEE = [
  "LIVE TRIVIA EVERY NIGHT",
  "PLAY FROM YOUR PHONE",
  "UGLY JOE'S · KANSAS CITY",
  "FRESH QUESTIONS NIGHTLY",
  "TAP INTO THE TRIVIA",
  "SPECIALS BETWEEN ROUNDS",
];

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Nav() {
  return (
    <header data-testid="landing-nav" className="fixed top-0 left-0 right-0 z-40">
      <div className="glass mx-auto mt-4 flex max-w-7xl items-center justify-between rounded-2xl px-5 py-3 mx-4 lg:mx-auto">
        <a href="#top" className="flex items-center sm:hidden">
          <LogoLockup className="h-9 w-auto" />
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold tracking-wide text-mut">
          <a className="transition-colors hover:text-magenta" href="#how">How it works</a>
          <a className="transition-colors hover:text-magenta" href="#bento">The show</a>
          <a className="transition-colors hover:text-magenta" href="#pricing">Pricing</a>
        </nav>
        <div className="flex items-center gap-4">
          <Link
            data-testid="nav-cta-login"
            to="/venue"
            className="neon-btn inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-extrabold uppercase tracking-wide"
          >
            Sign up or log in
          </Link>
          <a href="#top" className="hidden sm:flex items-center" data-testid="nav-brand-logo">
            <BrainTapLogo lockupClass="h-20" />
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative overflow-hidden px-5 pt-36 pb-20 lg:pt-44 lg:pb-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 18% 12%, rgba(255,43,253,0.2), transparent 45%), radial-gradient(circle at 82% 8%, rgba(168,56,255,0.26), transparent 50%), radial-gradient(circle at 55% 100%, rgba(0,229,255,0.12), transparent 55%)",
        }}
      />
      <Watermark />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            variants={reveal}
            initial="hidden"
            animate="show"
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-magenta/50 bg-magenta/10 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-[#ff9df2]"
          >
            <span className="live-dot inline-block h-2 w-2 rounded-full bg-coral" />
            Live pub trivia · every night
          </motion.p>
          <h1 className="font-display text-[13vw] font-black uppercase leading-[0.95] tracking-tight text-cream sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
            <MaskedLine delay={0.1}>Your local</MaskedLine>
            <MaskedLine delay={0.24}>pub is now a</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="grad-text">game show.</span>
            </MaskedLine>
          </h1>
          <motion.p
            variants={reveal}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-5 font-display text-lg font-bold uppercase tracking-[0.06em] text-coral text-glow-pink"
            data-testid="hero-tagline"
          >
            "Tap into the trivia, drink up the glory!"
          </motion.p>
          <motion.p
            variants={reveal}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-5 max-w-xl text-lg leading-relaxed text-mut"
          >
            The big screen hosts. The whole room plays along on their phones. Fresh questions
            every night, live scoring, drink specials between rounds — and the sharpest
            in the room takes the glory.
          </motion.p>
          <motion.div
            variants={reveal}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              data-testid="hero-cta-login"
              to="/venue"
              className="neon-btn group inline-flex items-center gap-3 rounded-full px-10 py-4 font-display text-base font-extrabold uppercase tracking-wide"
            >
              Sign up or log in
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <motion.div
            variants={reveal}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut"
          >
            <span><span className="text-amber">60</span> questions a game</span>
            <span><span className="text-amber">1000</span> points max</span>
            <span><span className="text-amber">1 tap</span> to install the app</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 1.5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border border-ice/15 shadow-[0_40px_80px_rgba(0,0,0,0.55)]">
              <motion.img
                data-testid="hero-image"
                src={HERO_IMG}
                alt="Pub trivia night crowd"
                style={{ y: imgY }}
                className="h-[420px] w-full scale-110 object-cover lg:h-[520px]"
              />
              <div className="absolute inset-0 bg-navy-900/45" />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy-900 to-transparent" />
              <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-navy-900/80 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-coral backdrop-blur">
                <span className="live-dot h-2 w-2 rounded-full bg-coral" /> On air
              </div>
            </div>

            <motion.div style={{ y: glowY }} className="absolute -left-6 top-24 hidden sm:block">
              <div className="glass float-slow flex items-center gap-3 rounded-2xl px-5 py-4">
                <Radio className="text-amber" size={20} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-mut">Game</div>
                  <div className="font-mono text-lg font-bold text-amber-soft">0248 · LIVE</div>
                </div>
              </div>
            </motion.div>

            <div className="glass float-slow absolute -bottom-8 -right-4 flex items-center gap-4 rounded-2xl p-5 sm:right-[-1.5rem]">
              <span className="live-dot h-3 w-3 rounded-full bg-coral shadow-[0_0_14px_#ff5a82]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-mut">Tonight at the bar</div>
                <div className="font-display text-xl font-extrabold uppercase text-amber-soft">Live trivia · big screen</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  return (
    <div data-testid="marquee" className="overflow-hidden border-y border-ice/10 bg-navy-800/70 py-5">
      <div className="marquee-track items-center">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center gap-10 pr-10">
            {MARQUEE.map((item) => (
              <span key={item} className="flex items-center gap-10 whitespace-nowrap font-display text-sm font-extrabold uppercase tracking-[0.3em] text-mut">
                {item}
                <span className="inline-block h-2.5 w-2.5 rotate-45 bg-amber" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function How() {
  const steps = [
    { n: "01", icon: ScanLine, title: "Claim your seat", body: "Scan the QR on the big screen or type the venue code. Sixty seconds later you have a name, an avatar, and a seat in the room." },
    { n: "02", icon: Timer, title: "Beat the clock", body: "Sixty questions, fifteen seconds each. Answer fast — points drain from 1000 to 100 as the clock burns. Back-to-back corrects build a streak multiplier." },
    { n: "03", icon: Brain, title: "Claim the glory", body: "Final standings hit the big screen between rounds. The winner's avatar taunts the whole venue. City, national and world boards follow." },
  ];
  return (
    <section id="how" className="px-5 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-amber">
          How it works
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-16 max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-cream sm:text-5xl">
          Three moves from<br />walk-in to <span className="text-amber">genius.</span>
        </motion.h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              custom={i}
              className="card-navy group relative overflow-hidden rounded-3xl p-8 transition-all hover:-translate-y-1 hover:border-amber/50 hover:shadow-[0_12px_40px_rgba(245,179,53,0.12)]"
              data-testid={`how-step-${s.n}`}
            >
              <div className="mb-8 flex items-center justify-between">
                <span className="font-mono text-5xl font-bold text-ice/15 transition-colors group-hover:text-amber/40">{s.n}</span>
                <s.icon size={26} className="text-amber" />
              </div>
              <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">{s.title}</h3>
              <p className="leading-relaxed text-mut">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScoreBars() {
  const bars = [1000, 780, 560, 340, 100];
  return (
    <div className="mt-6 flex h-40 items-end gap-3">
      {bars.map((v, i) => (
        <motion.div
          key={v}
          initial={{ height: 0 }}
          whileInView={{ height: `${(v / 1000) * 100}%` }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 rounded-t-lg"
          style={{ background: `linear-gradient(to top, rgba(245,179,53,0.15), #f5b335)` }}
        >
          <span className="mt-2 block text-center font-mono text-[11px] font-bold text-amber-soft">{v}</span>
        </motion.div>
      ))}
    </div>
  );
}

function Bento() {
  return (
    <section id="bento" className="grain relative px-5 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-amber">
          The show
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-16 max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-cream sm:text-5xl">
          A broadcast engine<br />dressed as <span className="text-amber">venue games.</span>
        </motion.h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="card-navy relative overflow-hidden rounded-3xl p-8 md:col-span-7" data-testid="bento-ai">
            <div className="flex items-start justify-between gap-6">
              <div>
                <Sparkles className="mb-6 text-amber" size={26} />
                <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">Fresh questions, every single night</h3>
                <p className="max-w-md leading-relaxed text-mut">
                  An AI quiz-writer pens new packs nightly across history, science, sports,
                  movies, music and geography. Never the same game twice.
                </p>
              </div>
              <div className="hidden shrink-0 overflow-hidden rounded-2xl border border-ice/15 sm:block">
                <img src={COUCH_IMG} alt="Playing trivia from the couch" className="h-44 w-56 object-cover" />
              </div>
            </div>
            <div className="mt-7 flex flex-wrap gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em]">
              {["History", "Science", "Sports", "Movies", "Music", "Geography"].map((c) => (
                <span key={c} className="rounded-full border border-ice/20 bg-navy-900/60 px-3.5 py-1.5 text-ice">{c}</span>
              ))}
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} custom={1} className="card-navy rounded-3xl p-8 md:col-span-5" data-testid="bento-scoring">
            <Timer className="mb-6 text-amber" size={26} />
            <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">Speed is everything</h3>
            <p className="leading-relaxed text-mut">Lock in instantly for 1000. Hesitate and watch the pot drain to 100.</p>
            <ScoreBars />
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="card-navy rounded-3xl p-8 md:col-span-4" data-testid="bento-join-code">
            <ScanLine className="mb-6 text-amber" size={26} />
            <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">Three taps to play</h3>
            <p className="mb-6 leading-relaxed text-mut">Every room gets a permanent join code. Players type it once and they're in the game.</p>
            <div className="mx-auto w-fit rounded-2xl border-2 border-amber/40 bg-amber/5 px-8 py-5 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-mut">Join code</div>
              <div className="font-mono text-4xl font-extrabold tracking-[0.2em] text-amber-soft">8 4 7 2 9 1</div>
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} custom={1} className="card-navy rounded-3xl p-8 md:col-span-4" data-testid="bento-branding">
            <Tv className="mb-6 text-amber" size={26} />
            <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">Your venue, your brand</h3>
            <p className="mb-6 leading-relaxed text-mut">Venues add their logo and push food &amp; drink specials straight onto the big screen.</p>
            <div className="rounded-2xl border-2 border-dashed border-amber/40 bg-amber/5 px-5 py-4 text-center font-display text-sm font-extrabold uppercase tracking-[0.3em] text-amber">
              Your logo here
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} custom={2} className="card-navy rounded-3xl p-8 md:col-span-4" data-testid="bento-boards">
            <Globe2 className="mb-6 text-amber" size={26} />
            <h3 className="mb-3 font-display text-2xl font-bold uppercase tracking-tight text-cream">Ladders to the glory</h3>
            <ul className="space-y-3">
              {[
                { icon: MapPin, label: "Your venue — tonight's board" },
                { icon: MapPin, label: "Your city & region" },
                { icon: Trophy, label: "Nationwide top scores" },
                { icon: Globe2, label: "Worldwide all-time brains" },
              ].map((row) => (
                <li key={row.label} className="flex items-center gap-3 rounded-xl border border-ice/10 bg-navy-900/50 px-4 py-3 text-sm font-semibold text-ice">
                  <row.icon size={15} className="shrink-0 text-amber" />
                  {row.label}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Intermission() {
  return (
    <section className="px-5 py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="relative lg:col-span-6">
          <div className="overflow-hidden rounded-3xl border border-ice/15 shadow-[0_40px_80px_rgba(0,0,0,0.5)]">
            <img src={FOOD_IMG} alt="Burger and fries special" className="h-[420px] w-full object-cover" />
            <div className="absolute inset-0 bg-navy-900/35" />
          </div>
          <div className="glass-amber absolute -bottom-6 left-8 flex items-center gap-3 rounded-2xl px-6 py-4 backdrop-blur">
            <UtensilsCrossed size={20} className="text-amber-soft" />
            <span className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-amber-soft">Specials on screen, sales at the table</span>
          </div>
        </motion.div>
        <div className="lg:col-span-6">
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-amber">
            The 10-minute empire
          </motion.p>
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-6 font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-cream sm:text-5xl">
            Intermission is<br /><span className="text-amber">prime time.</span>
          </motion.h2>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-8 max-w-xl text-lg leading-relaxed text-mut">
            Between games the big screen runs a full ten-minute break: the winner's avatar
            talking trash, your food and drink specials in rotation, and leaderboards stacking
            up — your venue, the city, the nation, the world.
          </motion.p>
          <motion.ul variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="space-y-4">
            {[
              { icon: Wine, text: "Push deals from your dashboard — wings, pitchers, burger bundles" },
              { icon: Trophy, text: "Rotating boards: venue, region, nationwide, worldwide" },
              { icon: Brain, text: "Winner moment: avatar + taunt, in giant format" },
            ].map((row) => (
              <li key={row.text} className="flex items-start gap-4 rounded-2xl border border-ice/10 bg-navy-800/60 px-5 py-4">
                <row.icon size={18} className="mt-1 shrink-0 text-amber" />
                <span className="font-semibold text-ice">{row.text}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="px-5 py-24 lg:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-4 text-center font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-amber">
          Pricing
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-16 text-center font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-cream sm:text-5xl">
          Two rooms.<br />Pick yours.
        </motion.h2>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} data-testid="pricing-home" className="card-navy flex flex-col rounded-3xl p-10">
            <span className="mb-6 w-fit rounded-full border border-ice/25 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-ice">At home</span>
            <div className="mb-6 flex items-end gap-2">
              <span className="font-display text-6xl font-extrabold text-cream">$9.99</span>
              <span className="pb-2 font-mono text-sm text-mut">/month</span>
            </div>
            <ul className="mb-10 space-y-3 text-mut">
              {["Private home room with its own join code", "House QR code on your TV, play from the couch", "Full live game: 10 questions, speed scoring", "Avatar builder + winner taunts", "Fresh AI questions every night"].map((f) => (
                <li key={f} className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber" />{f}</li>
              ))}
            </ul>
            <Link
              data-testid="pricing-home-cta"
              to="/venue?plan=home"
              className="mt-auto inline-flex justify-center rounded-full border-2 border-ice/25 px-8 py-4 font-display font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber"
            >
              Start a home room
            </Link>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} custom={1} data-testid="pricing-venue" className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-amber bg-gradient-to-b from-amber/15 to-navy-800 p-10 shadow-[0_20px_70px_rgba(245,179,53,0.18)]">
            <span className="absolute right-6 top-6 rounded-full bg-amber px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-navy-900">Most popular</span>
            <span className="mb-6 w-fit rounded-full border border-amber/50 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber-soft">At your venue</span>
            <div className="mb-6 flex items-end gap-2">
              <span className="font-display text-6xl font-extrabold text-amber">$29.99</span>
              <span className="pb-2 font-mono text-sm text-mut">/month</span>
            </div>
            <ul className="mb-10 space-y-3 text-ice">
              {["Everything in Home, plus:", "Big-screen broadcast mode for the whole venue", "Your logo + venue branding on screen", "Push food & drink specials into intermission", "City/region + nationwide leaderboard features", "Persistent venue QR — players scan and sit down"].map((f) => (
                <li key={f} className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber" />{f}</li>
              ))}
            </ul>
            <Link
              data-testid="pricing-venue-cta"
              to="/venue?plan=venue"
              className="neon-btn mt-auto inline-flex justify-center rounded-full px-8 py-4 font-display font-extrabold uppercase tracking-wide text-white"
            >
              Claim your venue
            </Link>
          </motion.div>
        </div>
        <p className="mt-8 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-mut">Billing launches with our first season — start playing free today</p>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden px-5 py-28 text-center">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 120%, rgba(245,179,53,0.2), transparent 55%)" }}
      />
      <div className="relative mx-auto max-w-4xl">
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mb-8 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-cream sm:text-6xl">
          Tonight, the glory<br />is <span className="text-amber text-glow-amber">up for grabs.</span>
        </motion.h2>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
          <Link
            data-testid="footer-cta-login"
            to="/venue"
            className="neon-btn inline-flex items-center gap-3 rounded-full px-10 py-5 font-display text-lg font-extrabold uppercase tracking-wide text-white"
          >
            Sign up or log in
            <Brain size={20} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

const DIFFICULTIES = ["4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "college"];
const TOGGLE_META = [
  ["intermission_code", "Intermission check-in code"],
  ["kick_absent", "Kick players who skip check-in"],
  ["takeover_video", "Mascot takeover video"],
  ["phone_sounds", "Phone sounds"],
  ["ads_on_tv", "Ads on TV"],
  ["home_play", "Play-at-home room"],
];

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="min-h-screen bg-navy-900 text-cream" data-testid="landing-page">
      <Nav />
      <Hero />
      <Marquee />
      <How />
      <Bento />
      <Intermission />
      <Pricing />
      <CtaBand />
      <footer className="border-t border-ice/10 px-5 py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 sm:flex-row sm:items-center">
          <LogoLockup className="h-8 w-auto" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut">
            Ugly Joe's is our favorite placeholder · Tap into the trivia, drink up the glory! · braintap.bar · {new Date().getFullYear()}
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <a href="mailto:braintaplive@gmail.com" data-testid="footer-contact-email" className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut transition-colors hover:text-magenta">Contact: braintaplive@gmail.com</a>
            <Link data-testid="footer-champions" to="/champions" className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut transition-colors hover:text-amber">Champions Wall</Link>
            <a href="/api" className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut transition-colors hover:text-amber">API health</a>
            <Link data-testid="footer-admin-link" to="/admin" className="text-[10px] uppercase tracking-[0.3em] text-mut/50 transition-colors hover:text-amber">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
